import "@shoelace-style/shoelace/dist/components/menu/menu.js";
import "@shoelace-style/shoelace/dist/components/menu-item/menu-item.js";
import "@shoelace-style/shoelace/dist/components/dialog/dialog.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/icon-button/icon-button.js";

import "../ConfirmDialog";
import "../PlaceMarker/PlaceMarker";
import "../CopyToClipboard";

import { html } from "lit";
import { LeafletEvent, LeafletMouseEvent, Map as LeafletMap, Marker } from "leaflet";
import { PropertyValues } from "@lit/reactive-element/reactive-element";
import { customElement, state, property } from "lit/decorators.js";
import { classMap } from "lit-html/directives/class-map.js";
import debounce from "@queso/debounce";

import * as L from "leaflet";

import "leaflet.tilelayer.cached";
import "leaflet.markercluster";
import "leaflet-rotatedmarker";
import "../../leaflet/leaflet.measure.js";
import "../../leaflet/leaflet.awesome-markers.js";

import { IMedia, IPlace } from "../../models/Models";
import { ILocation, LatLngToLocation, normalizeLocation } from "../../models/ILocation";
import { LocationType, MediaContentType } from "../../../models";
import { PlaceTable } from "../../backend/cloud/PlaceTable";

import { makeCustomEvent } from "../../Utils";
import { MapBounds } from "../../Kernel";

import { GEOMETRY_MEDIA_TYPES, loadGeoJsonFile } from "../../Media";
import { watch } from "../internal/watch";
import {
    OrientationResult,
    registerOrientationListener,
    unregisterOrientationListener,
} from "../../Orientation";
import { BaseElement } from "../BaseElement";
import { AllMapTypes, createTileLayer, MapType } from "../../TileLayers";
import { litIf } from "../LitUtils";
import { PlaceComponents } from "./PlaceComponents";
import { GeoJsonComponent } from "./GeoJsonComponent";
import { LayerComponent } from "./LayerComponent";
import { styles } from "./PlacesMap.style";
import { ChangeSet, ChangeSetListener } from "../../DataStore/ChangeSet";
import { DataProvider } from "../../DataStore/DataProvider";
import { Geometry, MediaGeometry } from "../../DataProviders/GeoProvider";

import { DataClient } from "../../DataStore/LocalDataStore";
import { compiledFilterPlaceAsync, compileFilters } from "../../Filter";
import { IPlaceFilter } from "../../backend/cloud/PlaceFilterTable";
import { LocationTypeToColor, LocationTypeToIcon } from "../PlaceMarker/PlaceMarker";
import { formatLocation } from "../../Format";
import { getModuleLog } from "../../Logger";

import { Mutex } from "async-mutex";

export const MAX_ZOOM = 19;

const SHOW_SURVEY_MARKERS_ZOOM = 16;
const CURRENT_POSITION_MARKER_ROTATION_OFFSET = -45;

export type MarkerType = "points" | "details";

enum FitBoundsFlags {
    None = 0,
    IgnoreCurrentLocation = 1,
}

function makeLocationTypeIcon(type: LocationType) {
    return (L as any).AwesomeMarkers.icon({
        icon: LocationTypeToIcon.get(type),
        markerColor: LocationTypeToColor.get(type),
    });
}

const poiMarker = new L.Icon({
    iconUrl: `./assets/user.png`,
    shadowUrl: "./assets/map-icons/marker-shadow.png",
    iconSize: [41, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

const locationTypeIcons: Record<LocationType, L.Icon> = {
    Unknown: makeLocationTypeIcon(LocationType.UNKNOWN),
    Depression: makeLocationTypeIcon(LocationType.DEPRESSION),
    NotFound: makeLocationTypeIcon(LocationType.NOT_FOUND),
    Cave: makeLocationTypeIcon(LocationType.CAVE),
    Sinkhole: makeLocationTypeIcon(LocationType.SINKHOLE),
    SolutionTube: makeLocationTypeIcon(LocationType.SOLUTION_TUBE),
    RockyOutcrop: makeLocationTypeIcon(LocationType.ROCKY_OUTCROP),
    Destroyed: makeLocationTypeIcon(LocationType.DESTROYED),
    Landmark: makeLocationTypeIcon(LocationType.LANDMARK),
    Mine: makeLocationTypeIcon(LocationType.MINE),
    Wreck: makeLocationTypeIcon(LocationType.WRECK),
    Reef: makeLocationTypeIcon(LocationType.REEF),
    Jetty: makeLocationTypeIcon(LocationType.JETTY),
    Muck: makeLocationTypeIcon(LocationType.MUCK),
    Sand: makeLocationTypeIcon(LocationType.SAND),
    OceanWall: makeLocationTypeIcon(LocationType.OCEAN_WALL),
    Campsite: makeLocationTypeIcon(LocationType.CAMPSITE),
};

export type MapPOI = {
    location: ILocation;
};

function mapBoundsToLeafletBounds(bounds: MapBounds | undefined): L.LatLngBounds | undefined {
    if (bounds) {
        return new L.LatLngBounds(
            new L.LatLng(bounds.sw.lat, bounds.sw.lon),
            new L.LatLng(bounds.ne.lat, bounds.ne.lon)
        );
    }
    return undefined;
}

type GeometryItem = {
    layer: LayerComponent;
    geo: Geometry;
};

type PlaceItem = {
    place: IPlace;
    components?: PlaceComponents;
};

/**
 * A simple web component wrapper with some functionality around the Leaflet.js map library.
 *
 * @link https://github.com/leaflet-extras/leaflet-map/
 * @link https://github.com/Gubancs/leaflet4vaadin
 * @link https://github.com/ggcity/leaflet-map
 *
 * @fires 'map-ready' - Event transporting an leaflet map instance. Fires using the `whenReady` event of leaflet map.
 *
 * @cssprop {Length} --leaflet-map-min-height - Min. height of the map element
 * @cssprop {Length} --leaflet-popup-item-spacing - Spacing of title + url items inside marker popup
 */
@customElement("ds-places-map")
export class PlacesMap extends BaseElement implements ChangeSetListener {
    static override styles = styles;

    @property({ type: Boolean }) persistent = false;
    @property({ type: Boolean }) detectRetina = true;
    @property({ type: Number }) defaultZoom = 11;
    @property({ type: Number }) gotoLocationZoom = 16;
    @property({ type: String }) mapType: MapType = this._kernel.mapTileLayer.get();
    @property({ type: Boolean }) showCurrentPosition = false;
    @property({ type: Boolean }) trackCurrentPosition = false;
    @property({ type: Array }) placesOfInterest: MapPOI[] = [];
    @property({ type: Boolean }) noMyLocationButton = false;
    @property({ type: Object }) mapCenter?: ILocation;
    @property({ type: Object }) mapBounds?: MapBounds;
    @property({ type: Number }) mapZoom: number | undefined = 11;
    @property({ type: Boolean }) noControls = false;
    @property({ type: Boolean }) showExitNavigationButton = false;
    @property({ type: String }) markerType: MarkerType = "details";
    @property({ type: Boolean }) showDebug = false;

    @state() protected _currentPosMarker?: Marker;
    @state() protected _movingPlaceMarker?: Marker;
    @state() protected _movingPlaceId?: string;
    @state() protected _currentMapType: MapType = this._kernel.mapTileLayer.get();
    @state() protected _trackingCentered = true;
    @state() protected _currentTrackingLocation?: ILocation;
    @state() protected _numFilteredPlaces = 0;
    @state() protected _numPlaces = 0;
    @state() protected _currentZoom = 0;

    private _log = getModuleLog("places-map");

    private _currentBounds: L.LatLngBounds | undefined = undefined;
    private _orientationListenerId: string | undefined;
    private _placesOfInterest: Marker[] = [];
    private _map!: LeafletMap;
    private _mapClickDelayedTimeout: number | null = null;
    private _markerGroupLayer: any = undefined;
    private _placeComponents = new Map<string, PlaceItem>();
    private _tiles?: L.Layer;
    private _places = this._kernel.dataStore.getSource<PlaceTable>("places");
    private _filter = compileFilters(this._kernel.currentPlaceFilter.get().filter);
    private _surveyMarkers = new L.LayerGroup();
    private _geoItems = new Map<string, GeometryItem>();
    private _homeMarker?: Marker;

    private _updateMutex = new Mutex();
    private debouncedResize: () => unknown = () => false;

    constructor() {
        super();
        (L as any).AwesomeMarkers.Icon.prototype.options.prefix = "fa";
    }

    @watch("markerType")
    async _markerTypeChanged() {
        await this._rebuildMarkers();
    }

    @watch("mapCenter")
    _mapCenterChanged() {
        if (this._map && this.mapCenter) {
            this.goTo(this.mapCenter, this.mapZoom);
        }
    }

    @watch("mapZoom")
    _mapZoomChanged() {
        if (this._map && this.mapZoom) {
            this._map.setZoom(this.mapZoom);
        }
    }

    @watch("mapBounds")
    _mapBoundsChanged() {
        if (this._map && this.mapBounds) {
            const bounds = mapBoundsToLeafletBounds(this.mapBounds);
            if (bounds) {
                this._map.fitBounds(bounds);
            }
        }
    }

    @watch("trackCurrentPosition")
    _trackPositionChanged() {
        this._trackingCentered = true;
        if (this.trackCurrentPosition) {
            this._orientationListenerId = registerOrientationListener((result: OrientationResult) =>
                this._orientationChanged(result)
            );
        } else {
            if (this._orientationListenerId !== undefined) {
                unregisterOrientationListener(this._orientationListenerId);
                this._orientationListenerId = undefined;
            }
            this._currentTrackingLocation = undefined;
            this._deleteCurrentPositionMarker();
        }
    }

    @watch("placesOfInterest")
    _placesOfInterestChanged(_oldValue: any, newValue: any) {
        //TODO: make this nicer and don't destroy ones that are still in the new set
        this._clearPlacesOfInterest();
        this._createPlacesOfInterest(newValue);
    }

    private _updateHomeMarker() {
        if (this._homeMarker) {
            this._homeMarker.removeFrom(this._map);
            this._homeMarker = undefined;
        }
        const home = this._kernel.currentHomeLocation.get();
        if (home) {
            this._homeMarker = L.marker([home.lat, home.lon], {
                icon: new L.DivIcon({
                    html: `<sl-icon name="car-front" class="home-icon-marker"></sl-icon>`,
                    className: "home-icon-marker",
                }),
            });
            this._homeMarker.on("click", () => {
                this._raiseEvent("ds-home-selected");
            });
            this._homeMarker.addTo(this._map);
        }
    }

    private async _createGeoMediaLayer(file: IMedia) {
        const geoJson = await loadGeoJsonFile(file);
        if (geoJson) {
            // default to MediaContentType.SURVEY_DATA
            let color = "#DD4E4D";
            let weight = 4;

            if (file.contentType) {
                if (file.contentType === MediaContentType.TRACK_DATA) {
                    color = "#3388ff";
                    weight = 2;
                }
            }

            return new GeoJsonComponent(
                this._map,
                this._surveyMarkers,
                file.id,
                JSON.stringify(geoJson),
                color,
                weight
            );
        }
        return undefined;
    }

    private async _createGeoItem(item: Geometry) {
        let layer: LayerComponent | undefined;
        const mediaGeo = item as MediaGeometry;

        if (GEOMETRY_MEDIA_TYPES.includes(item.type)) {
            layer = await this._createGeoMediaLayer(mediaGeo.media);
        }

        if (layer) {
            const newItem = {
                layer,
                geo: item,
            } as GeometryItem;

            if (item) {
                this._geoItems.set(item.id, newItem);
                newItem.layer.attachLayer();
            }
        }
    }

    private _deleteGeoItem(deleted: Geometry) {
        const item = this._geoItems.get(deleted.id);
        if (item) {
            item.layer.detachLayer();
            this._geoItems.delete(deleted.id);
        }
    }

    private _createPlaceMarker(place: IPlace) {
        const data = place.data;
        const location = normalizeLocation(data.location);
        let leafletMarker;

        if (this.markerType === "details") {
            leafletMarker = L.marker([location.lat, location.lon], {
                icon: new L.DivIcon({
                    html: `<ds-place-marker placeId="${place.id}"></ds-place-marker>`,
                    className: "div-icon-marker",
                }),
                riseOnHover: true,
            });
        } else {
            leafletMarker = L.marker([location.lat, location.lon], {
                icon: locationTypeIcons[place.data.spelioData.type as LocationType],
                title: place.data.name,
            });
        }
        this._markerGroupLayer.addLayer(leafletMarker);
        leafletMarker.on("click", () => {
            this._openEditor(place);
        });

        return leafletMarker;
    }

    private async _createPlaceItem(place: IPlace): Promise<void> {
        let components: PlaceComponents | undefined;
        if (!(await compiledFilterPlaceAsync(this._filter, place))) {
            components = new PlaceComponents();
            this._componentCreateMarker(place, components);

            ++this._numFilteredPlaces;
        }
        this._placeComponents.set(place.id, {
            place,
            components,
        });

        ++this._numPlaces;
    }

    private async _updatePlaceItem(oldValue: IPlace, newValue: IPlace) {
        this._deletePlaceItem(oldValue);
        await this._createPlaceItem(newValue);
    }

    private _componentCreateMarker(place: IPlace, components: PlaceComponents) {
        if (!components.marker) {
            components.marker = this._createPlaceMarker(place);
            components.markerType = place.data.spelioData.type as LocationType;
            components.markerName = place.data.name;
        }
    }

    private _componentDeleteMarker(components: PlaceComponents) {
        if (components.marker) {
            this._markerGroupLayer.removeLayer(components.marker);
            components.marker.remove();
            components.marker = undefined;
        }
        components.markerType = undefined;
    }

    private _deletePlaceItem(place: IPlace) {
        const item = this._placeComponents.get(place.id);
        if (item) {
            if (item.components) {
                this._componentDeleteMarker(item.components);
                item.components.clearAllLayers();
                --this._numFilteredPlaces;
            }
            this._placeComponents.delete(place.id);
        }
        --this._numPlaces;
    }

    async onChangeSet(source: DataProvider, changeSet: ChangeSet<any>) {
        await this._updateMutex.runExclusive(async () => {
            if (source.name === "geometry") {
                for (const deleted of changeSet.deleted) {
                    this._deleteGeoItem(deleted);
                }

                for (const created of changeSet.created) {
                    await this._createGeoItem(created);
                }

                for (const updated of changeSet.updated) {
                    this._deleteGeoItem(updated.oldValue);
                    this._createGeoItem(updated.newValue);
                }
            } else if (source.name === "places") {
                for (const deleted of changeSet.deleted) {
                    this._deletePlaceItem(deleted);
                }

                for (const created of changeSet.created) {
                    await this._createPlaceItem(created);
                }

                for (const updated of changeSet.updated) {
                    await this._updatePlaceItem(updated.oldValue, updated.newValue);
                }

                this._raiseNumShowingEvent();
            }
        });
    }

    private async _rebuildMarkers() {
        await this._updateMutex.runExclusive(async () => {
            for (const item of this._placeComponents.values()) {
                if (item.components) {
                    this._componentDeleteMarker(item.components);
                    this._componentCreateMarker(item.place, item.components);
                }
            }
        });
    }

    private async _refilterExisting() {
        await this._updateMutex.runExclusive(async () => {
            for (const item of this._placeComponents.values()) {
                const show = !(await compiledFilterPlaceAsync(this._filter, item.place));
                if (!show && item.components) {
                    this._componentDeleteMarker(item.components);
                    item.components = undefined;
                    this._numFilteredPlaces--;
                } else if (show && !item.components) {
                    item.components = new PlaceComponents();
                    this._componentCreateMarker(item.place, item.components);
                    this._numFilteredPlaces++;
                }
            }
        });

        this._raiseNumShowingEvent();
    }

    override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        const [debounced] = debounce(() => {
            this._handleResize();
        }, 200);
        this.debouncedResize = debounced;

        this._subscribeToKernelEvent(
            "current-place-filter-changed",
            async (filter: IPlaceFilter) => {
                this._filter = compileFilters(filter.filter);

                // clear all existing markers and recreate them
                await this._refilterExisting();

                // If we are filtering by nameOrId then the user has entered text in the search
                // box and we will zoom to the results immediately
                if (
                    this._kernel.zoomOnFilterChange.get() ||
                    (filter.filter.nameOrIdQuery && filter.filter.nameOrIdQuery.length > 0)
                ) {
                    this._fitBounds(FitBoundsFlags.IgnoreCurrentLocation);
                }

                this._log.debug("filter-changed: updating places");
            }
        );

        this._subscribeToKernelEvent("ds-location-changed", (location: ILocation) =>
            this._onLocationChanged(location)
        );

        this.addEventListener("resize", this.debouncedResize);

        this._subscribeToKernelEvent("home-location", () => this._updateHomeMarker());
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        if (this._orientationListenerId) {
            unregisterOrientationListener(this._orientationListenerId);
            this._orientationListenerId = undefined;
        }
        this._kernel.dataStore.disconnectClient(this);
    }

    private _raiseNumShowingEvent() {
        this._raiseEvent("map-num-showing-changed", {
            count: this._numFilteredPlaces,
        });
    }

    public goTo(location: ILocation, zoom: number | undefined = undefined) {
        location = normalizeLocation(location);
        const latLng = {
            lat: location.lat,
            lng: location.lon,
        };
        if (zoom) {
            this._map.setView(latLng, zoom);
        } else {
            this._map.setView(latLng);
        }
    }

    override async firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);

        const mapDomElement = this.renderRoot.querySelector(".map") as HTMLElement;
        if (!mapDomElement) {
            return;
        }

        this._map = L.map(mapDomElement, {
            maxBoundsViscosity: 1.0,
            //maxBounds: L.latLngBounds(L.latLng(-180, -180), L.latLng(180, 180)),
            zoomControl: false,
            preferCanvas: true,
            worldCopyJump: true,
            measureControl: {
                enabled: true,
                kernel: this._kernel,
            },
        } as unknown as L.MapOptions);

        this._map.addLayer(this._surveyMarkers);
        this._updateHomeMarker();
        mapDomElement.style.isolation = "isolate";

        // Delayed click handler so we do not interfere with double clicks
        // @see https://github.com/Outdooractive/leaflet-singleclick_0.7
        this._map.on("contextmenu", (e) => this._onMapClickDelayed(e));
        this._map.on("dblclick", () => this._clearMapClickDelayedTimeout());
        this._map.on("moveend", () => this._saveCurrentLocation());
        this._map.on("zoomend", () => {
            const showingMarkers = this._map.hasLayer(this._surveyMarkers);
            if (this._map.getZoom() > SHOW_SURVEY_MARKERS_ZOOM) {
                if (!showingMarkers) {
                    this._map.addLayer(this._surveyMarkers);
                }
            } else if (showingMarkers) {
                this._map.removeLayer(this._surveyMarkers);
            }
            this._saveCurrentLocation();
            this._currentZoom = this._map.getZoom();
        });
        this._map.on("dragstart", () => {
            if (this.trackCurrentPosition) {
                this._trackingCentered = false;
            }
        });

        // Fire `map-ready` event using the `whenReady` Leaflet map event
        this._map.whenReady(async () => this._onMapReady());

        //"https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",

        this._loadTiles();

        // Add scale controls
        L.control.scale().setPosition("bottomright").addTo(this._map);

        this._markerGroupLayer = new L.MarkerClusterGroup({
            chunkedLoading: true,
            disableClusteringAtZoom: 14,
        });
        this._map.addLayer(this._markerGroupLayer);

        // Center map and update layers
        if (this.mapBounds) {
            this._mapBoundsChanged();
        } else if (this.mapCenter) {
            this._mapZoomChanged();
            this._mapCenterChanged();
        } else {
            this._currentBounds = mapBoundsToLeafletBounds(this._kernel.getMapBounds());
            this._fitBounds();
        }

        // Give the browser a chance to paint and trigger a resize
        // Should fix some issues with initial renders
        // @see https://github.com/Polymer/lit-element/commit/8f1ee40ea0349f320f19258b17ae7f776338e198 → "Add event listeners after first paint"
        await new Promise((r) => setTimeout(r, 0));
        window.dispatchEvent(new Event("resize"));

        this._kernel.dataStore.connectClient(
            new DataClient(this, [{ source: "geometry", paths: [] }])
        );

        this._kernel.dataStore.connectClient(
            new DataClient(this, [{ source: "places", paths: [] }])
        );
    }

    private _loadTiles() {
        if (this._tiles) {
            this._tiles.removeFrom(this._map);
            this._tiles = undefined;
        }

        this._tiles = createTileLayer(this.mapType).addTo(this._map);
    }

    override async updated(changedProperties: PropertyValues) {
        super.updated(changedProperties);

        this._updateMapSize();

        // Center map and update marker whenever the lat / long properties change
        if (
            changedProperties.has("latitude") ||
            changedProperties.has("longitude") ||
            changedProperties.has("radius") ||
            changedProperties.has("markers")
        ) {
            this._fitBounds(FitBoundsFlags.IgnoreCurrentLocation);
        }

        if (changedProperties.has("mapType")) {
            this._loadTiles();
        }
    }

    private _clearPlacesOfInterest() {
        this._placesOfInterest.forEach((p) => p.removeFrom(this._map));
        this._placesOfInterest = [];
    }

    private _createPlacesOfInterest(pois: MapPOI[]) {
        for (const p of pois) {
            const marker = L.marker([p.location.lat, p.location.lon], {
                icon: poiMarker,
                zIndexOffset: -100,
            }).addTo(this._map);
            this._placesOfInterest.push(marker);
        }
    }

    private _createCurrentPositionMarker(location: ILocation) {
        if (!this._currentPosMarker) {
            const icon = L.divIcon({
                html: '<i class="fa fa-location-arrow fa-3x"></i>',
                iconSize: [36, 36],
                iconAnchor: [18, 18],
                className: "current-location-icon",
            });

            this._currentPosMarker = L.marker([location.lat, location.lon], {
                icon,
            }).addTo(this._map);
            (this._currentPosMarker as any).setRotationOrigin("center center");
            (this._currentPosMarker as any).setRotationAngle(
                CURRENT_POSITION_MARKER_ROTATION_OFFSET
            );
        }
    }

    private _deleteCurrentPositionMarker() {
        if (this._currentPosMarker) {
            this._currentPosMarker.remove();
            this._currentPosMarker = undefined;
        }
    }

    private _onLocationChanged(location: ILocation) {
        if (this.showCurrentPosition) {
            if (!this._currentPosMarker) {
                this._createCurrentPositionMarker(location);
            } else {
                this._currentPosMarker.setLatLng([location.lat, location.lon]);
            }
        }

        if (this.trackCurrentPosition && this._map && this._trackingCentered) {
            const zoom = this._map.getZoom() || this.defaultZoom;
            this._map.setView([location.lat, location.lon], zoom);
        }

        this._currentTrackingLocation = location;
    }

    private _recenterMap() {
        this._trackingCentered = true;
        if (this._currentTrackingLocation) {
            const zoom = this._map.getZoom() || this.defaultZoom;
            this._map.setView(
                [this._currentTrackingLocation.lat, this._currentTrackingLocation.lon],
                zoom
            );
        }
    }

    private _orientationChanged(result: OrientationResult) {
        if (this._currentPosMarker) {
            (this._currentPosMarker as any).setRotationAngle(
                (result.heading + CURRENT_POSITION_MARKER_ROTATION_OFFSET) % 360
            );
        }
    }

    private _clearMovingMarker() {
        if (this._movingPlaceMarker) {
            this._movingPlaceMarker?.remove();
            this._movingPlaceMarker = undefined;
        }
        this._movingPlaceId = undefined;
    }

    private async _cancelMovingMarker() {
        this._clearMovingMarker();
        this._map.addLayer(this._markerGroupLayer);
    }

    public async startPlaceMove(placeId: string) {
        const place = await this._places.read(placeId);
        if (place) {
            // hide existing markers
            this._map.removeLayer(this._markerGroupLayer);
            this._clearMovingMarker();

            // create new draggable marker for moving place
            this._movingPlaceMarker = L.marker([place.data.location.lat, place.data.location.lon], {
                icon: locationTypeIcons[place.data.spelioData.type as LocationType],
                title: place.data.name,
                riseOnHover: true,
                draggable: true,
            }).addTo(this._map);
            this._movingPlaceId = placeId;

            // zoom to the place
            this._map.flyTo([place.data.location.lat, place.data.location.lon], 18);
        }
    }

    private _hasValidMapData() {
        return this._map;
    }

    private _fitBounds(flags: FitBoundsFlags = FitBoundsFlags.None) {
        if (!this._hasValidMapData()) {
            return;
        }

        let bounds = undefined;
        if (!((flags & FitBoundsFlags.IgnoreCurrentLocation) > 0)) {
            bounds = this._currentBounds;
        }

        if (!bounds) {
            if (this._markerGroupLayer) {
                bounds = this._markerGroupLayer.getBounds();
            }
        }

        if (bounds) {
            try {
                this._map.fitBounds(bounds);
            } catch (_e) {
                bounds = undefined;
            }
        }

        if (!bounds) {
            this._map.setView(
                {
                    lat: -37.8138,
                    lng: 140.79629,
                },
                10
            );
        }
    }

    private _saveCurrentLocation() {
        if (this._map && this.persistent) {
            const mapCenter = LatLngToLocation(this._map.getCenter());
            this.dispatchEvent(
                makeCustomEvent("map-center-changed", {
                    center: mapCenter,
                })
            );
            const bounds = this._map.getBounds();
            this._saveBounds(bounds);
        }
    }

    private _saveBounds(bounds: L.LatLngBounds) {
        if (this.persistent) {
            const sw = LatLngToLocation(bounds.getSouthWest());
            const ne = LatLngToLocation(bounds.getNorthEast());

            this._kernel.setMapBounds(sw, ne);
        }
    }

    private _onMapClick(e: LeafletEvent) {
        const event = e as LeafletMouseEvent;

        this.dispatchEvent(
            makeCustomEvent("map-location-selected", {
                location: {
                    lon: event.latlng.lng,
                    lat: event.latlng.lat,
                },
            })
        );
    }

    /**
     * Invalidates the map size and updates the map.
     * This is particularly useful when the map container size changed.
     */
    private _updateMapSize() {
        if (!this._map) {
            return;
        }

        this._map.invalidateSize();
    }

    private _onMapClickDelayed(e: LeafletEvent) {
        this._clearMapClickDelayedTimeout();

        this._mapClickDelayedTimeout = window.setTimeout(() => {
            this._onMapClick(e);
        }, 250);
    }

    private _onMapReady() {
        this.dispatchEvent(
            new CustomEvent("map-ready", {
                detail: {
                    map: this._map,
                },
            })
        );
    }

    private _clearMapClickDelayedTimeout() {
        if (!this._mapClickDelayedTimeout) {
            return;
        }

        clearTimeout(this._mapClickDelayedTimeout);
        this._mapClickDelayedTimeout = null;
    }

    private _handleResize() {
        if (!this._hasValidMapData()) {
            return;
        }

        this._updateMapSize();
        this._fitBounds();
    }

    private _openEditor(place: IPlace) {
        this.dispatchEvent(
            new CustomEvent("place-selected", {
                bubbles: true,
                composed: true,
                detail: {
                    placeId: place.id,
                },
            })
        );
    }

    private _zoomIn() {
        if (this._map) {
            this._map.zoomIn();
        }
    }

    private _zoomOut() {
        if (this._map) {
            this._map.zoomOut();
        }
    }

    private _goToCurrentLocation() {
        this._map.locate().on("locationfound", (e) => {
            const location = LatLngToLocation(e.latlng);
            this.goTo(location, this.gotoLocationZoom);
            this._createCurrentPositionMarker(location);
        });
    }

    private _nextMapType() {
        let index = AllMapTypes.findIndex((type) => type === this._currentMapType);
        if (index !== -1) {
            ++index;
            if (index > AllMapTypes.length - 1) {
                index = 0;
            }
            this._currentMapType = AllMapTypes[index];
        }

        this.mapType = this._currentMapType;
        this._raiseEvent("ds-map-type-changed", {
            type: this.mapType,
        });
    }

    override render() {
        return html`
            <link rel="stylesheet" href="/assets/leaflet.css" />
            <link rel="stylesheet" href="/assets/awesome-markers/leaflet.awesome-markers.css" />
            <div class="container">
                <div class="map"></div>
                ${litIf(
                    !this.noControls,
                    () =>
                        html` <div class="map-buttons flex-column">
                                <sl-icon-button
                                    name="plus"
                                    label="Zoom in"
                                    @click=${() => this._zoomIn()}
                                ></sl-icon-button>
                                <sl-icon-button
                                    name="dash-lg"
                                    label="Zoom out"
                                    @click=${() => this._zoomOut()}
                                ></sl-icon-button>
                                ${litIf(
                                    !this.noMyLocationButton,
                                    () => html`
                                        <sl-icon-button
                                            name="send"
                                            label="Track current position"
                                            class="${classMap({
                                                tracking: this.trackCurrentPosition,
                                                "not-tracking": !this.trackCurrentPosition,
                                            })}"
                                            @click=${() => {
                                                if (!this.trackCurrentPosition) {
                                                    this._goToCurrentLocation();
                                                }
                                                this._raiseEvent("ds-track-location", {
                                                    enableTracking: !this.trackCurrentPosition,
                                                });
                                            }}
                                        ></sl-icon-button>
                                    `
                                )}
                                <sl-icon-button
                                    name="stack"
                                    label="Change map type"
                                    @click=${() => this._nextMapType()}
                                ></sl-icon-button>
                                <sl-icon-button
                                    name="crop"
                                    label="fit map to filtered places"
                                    @click=${() =>
                                        this._fitBounds(FitBoundsFlags.IgnoreCurrentLocation)}
                                ></sl-icon-button>
                            </div>
                            <div class="top-center-controls flex-column">
                                ${litIf(
                                    this.showExitNavigationButton,
                                    () =>
                                        html`<sl-button
                                            pill
                                            variant="success"
                                            @click=${() => this._raiseEvent("ds-stop-navigation")}
                                            >Exit navigation</sl-button
                                        > `
                                )}
                            </div>
                            <div class="bottom-left-controls flex-column">
                                ${litIf(
                                    !!this._movingPlaceMarker,
                                    () => html`
                                        <sl-button
                                            pill
                                            variant="success"
                                            @click=${() => {
                                                this.dispatchEvent(
                                                    makeCustomEvent("place-move-end", {
                                                        location: {
                                                            lat: this._movingPlaceMarker?.getLatLng()
                                                                .lat,
                                                            lon: this._movingPlaceMarker?.getLatLng()
                                                                .lng,
                                                        } as ILocation,
                                                        placeId: this._movingPlaceId,
                                                    })
                                                );
                                                this._cancelMovingMarker();
                                            }}
                                            >Accept move</sl-button
                                        >
                                        <sl-button
                                            pill
                                            variant="danger"
                                            @click=${() => {
                                                this._cancelMovingMarker();
                                                this.dispatchEvent(
                                                    makeCustomEvent("place-move-cancel")
                                                );
                                            }}
                                            >Cancel move</sl-button
                                        >
                                    `
                                )}
                                ${litIf(
                                    !this._trackingCentered && this.trackCurrentPosition,
                                    () => html`
                                        <sl-button
                                            pill
                                            class="recenter-map-button"
                                            size="medium"
                                            @click=${() => this._recenterMap()}
                                            >Recenter map</sl-button
                                        >
                                    `
                                )}
                                <div class="flex-row">
                                    ${litIf(
                                        this._numPlaces > 0,
                                        () => html`
                                            <span class="statistic ds-border"
                                                >${this._numFilteredPlaces} of
                                                ${this._numPlaces}</span
                                            >
                                        `
                                    )}
                                    ${litIf(
                                        this.showDebug,
                                        () =>
                                            html` <span class="statistic ds-border"
                                                >Zoom: ${this._currentZoom}</span
                                            >`
                                    )}
                                </div>
                            </div>`
                )}
                ${litIf(
                    this.trackCurrentPosition,
                    () =>
                        html`<div class="current-location">
                            ${litIf(
                                !!this._currentTrackingLocation,
                                () =>
                                    html`<span class="current-location"
                                            >${formatLocation(
                                                this._currentTrackingLocation
                                            )}<ds-copy-to-clipboard
                                                .text=${formatLocation(
                                                    this._currentTrackingLocation
                                                )}
                                            ></ds-copy-to-clipboard></span
                                        >}`
                            )}
                        </div>`
                )}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-places-map": PlacesMap;
    }
}
